import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";

// Wrap any span/heading/paragraph/etc. and expose an inline-edit pencil for admins.
// props:
//   value        - current text
//   onSave(v)    - async callback to persist
//   as           - html tag to render (default 'span')
//   label        - dialog title / tooltip
//   multiline    - textarea instead of input
//   inputType    - 'text' | 'number'
//   className, children (rendered inside the wrapper — usually the same as value styled)
export default function EditableText({
  value,
  onSave,
  as: Tag = "span",
  label = "Text",
  multiline = false,
  inputType = "text",
  className = "",
  children,
  ...rest
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDraft(String(value ?? ""));
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const out = inputType === "number" ? Number(draft) : draft;
      await onSave(out);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const inner = (
    <Tag
      className={`${className} ${isAdmin ? "relative inline group/edit-text" : ""}`}
      {...rest}
    >
      {children ?? value}
      {isAdmin && (
        <button
          type="button"
          onClick={handleOpen}
          className="ml-2 inline-flex items-center opacity-0 group-hover/edit-text:opacity-100 focus:opacity-100 transition-opacity align-middle bg-white/95 hover:bg-white text-ink shadow-sm px-1.5 py-1 text-[0.65rem]"
          title={`Edit ${label}`}
          data-testid="editable-text-pencil"
        >
          <Pencil size={10} strokeWidth={1.6} />
        </button>
      )}
    </Tag>
  );

  return (
    <>
      {inner}
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Edit {label}</DialogTitle>
              <DialogDescription className="text-xs text-ink-soft">
                Changes go live on the storefront immediately.
              </DialogDescription>
            </DialogHeader>
            {multiline ? (
              <Textarea
                rows={8}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="editable-text-input"
                className="font-sans"
              />
            ) : (
              <Input
                type={inputType}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                data-testid="editable-text-input"
              />
            )}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setOpen(false)}
                className="overline px-4 py-2.5 border border-line hover:border-ink"
                data-testid="editable-text-cancel"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-ink px-6 py-2.5 overline disabled:opacity-50"
                data-testid="editable-text-save"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
